import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiChevronsUp, FiCopy, FiExternalLink, FiHelpCircle, FiKey, FiShield, FiSmartphone } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosInstance';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { ThemeToggle } from '../../components/AppShell/ThemeToggle';

const challengeKey = 'twoFactorChallenge';
const actionKey = 'twoFactorAction';
const emptyOtpDigits = () => Array(6).fill('');

export const TwoFactorChallenge = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [challengeToken] = useState(() => window.sessionStorage.getItem(challengeKey) || '');
  const [action] = useState(() => window.sessionStorage.getItem(actionKey) || 'verify');
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [otpDigits, setOtpDigits] = useState(emptyOtpDigits);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completion, setCompletion] = useState(null);
  const otpInputRefs = useRef([]);

  const cancel = useCallback(() => {
    window.sessionStorage.removeItem(challengeKey);
    window.sessionStorage.removeItem(actionKey);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!challengeToken) {
      cancel();
      return undefined;
    }
    if (action !== 'setup') return undefined;
    let active = true;
    axiosInstance('/user/login/2fa/setup', 'post', { challengeToken })
      .then((response) => {
        if (!active) return;
        if (response?.status === 200) setSetup(response.data);
        else setError(t(response?.data?.message || 'Unable to start two-factor setup.'));
      })
      .catch(() => active && setError(t('Unable to start two-factor setup.')));
    return () => { active = false; };
  }, [action, cancel, challengeToken, t]);

  const completeLogin = () => {
    window.sessionStorage.removeItem(challengeKey);
    window.sessionStorage.removeItem(actionKey);
    navigate('/dashboard', { replace: true });
  };

  const copy = async (value) => {
    try { await navigator.clipboard.writeText(value); toast.success(t('Copied to clipboard')); } catch (_error) { toast.error(t('Unable to copy')); }
  };

  const submit = async (event) => {
    event.preventDefault();
    const authenticationCode = otpDigits.join('');
    const submittedCode = useRecoveryCode ? code.trim() : authenticationCode;
    if (submitting || !submittedCode) {
      if (!submittedCode) setError(t('Enter your authentication code'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await axiosInstance('/user/login/2fa', 'post', {
        challengeToken,
        ...(useRecoveryCode ? { recoveryCode: submittedCode } : { code: authenticationCode }),
      });
      if (response?.status !== 200) throw new Error(response?.data?.message || 'Invalid authentication code.');
      if (response.data.setupCompleted) {
        setCompletion(response.data);
        return;
      }
      completeLogin(response.data);
    } catch (requestError) {
      setError(t(requestError.message || 'Invalid authentication code.'));
    } finally { setSubmitting(false); }
  };

  const title = action === 'setup' ? t('Set up your authenticator') : t('Verify your identity');
  const description = action === 'setup'
    ? t('Scan the QR code, then enter the six-digit code generated by your authenticator app.')
    : t('Enter the six-digit code from your authenticator app.');

  const updateOtpDigits = (index, value) => {
    const incomingDigits = value.replace(/\D/g, '').slice(0, 6 - index);
    setOtpDigits((current) => {
      const next = [...current];
      if (!incomingDigits) next[index] = '';
      else incomingDigits.split('').forEach((digit, offset) => { next[index + offset] = digit; });
      return next;
    });
    const nextIndex = Math.min(index + Math.max(incomingDigits.length, 1), 5);
    if (incomingDigits) window.requestAnimationFrame(() => otpInputRefs.current[nextIndex]?.focus());
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) otpInputRefs.current[index - 1]?.focus();
    if (event.key === 'ArrowLeft' && index > 0) { event.preventDefault(); otpInputRefs.current[index - 1]?.focus(); }
    if (event.key === 'ArrowRight' && index < 5) { event.preventDefault(); otpInputRefs.current[index + 1]?.focus(); }
  };

  return (
    <main className='login-screen relative flex h-[100dvh] w-full items-center justify-center overflow-hidden px-4 py-4 sm:px-8 sm:py-8'>
      <div className='absolute right-4 top-4 z-10 flex items-center gap-2 sm:right-8 sm:top-8'>
        {action === 'setup' && (
          <div className='relative'>
            <a
              href='/two-factor/tutorial'
              target='_blank'
              rel='noreferrer'
              className='two-factor-tutorial-trigger inline-flex min-h-10 items-center justify-center gap-2 rounded-[var(--radius-md)] border border-[var(--brand)]/45 bg-[var(--surface-panel)] px-3 py-2 text-sm font-semibold text-[var(--brand-strong)] shadow-sm hover:bg-[var(--brand-soft)] hover:text-[var(--brand-strong)]'
              aria-label={t('Open tutorial')}
            >
              <FiHelpCircle aria-hidden='true' />
              <span className='hidden sm:inline'>{t('Tutorial')}</span>
              <FiExternalLink aria-hidden='true' />
            </a>
            <div className='pointer-events-none absolute left-1/2 top-full mt-1 flex w-max -translate-x-1/2 flex-col items-center' aria-hidden='true'>
              <FiChevronsUp className='two-factor-tutorial-arrow h-7 w-7 text-[var(--brand)]' />
              <span className='-mt-1 rounded-full border border-[var(--brand)]/30 bg-[var(--surface-panel)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--brand-strong)] shadow-lg'>
                {t('Need help?')}
              </span>
            </div>
          </div>
        )}
        <ThemeToggle label={t('Toggle theme')} />
      </div>
      <div className='two-factor-scale w-full max-w-[440px]'>
      <section className='login-card two-factor-card w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel)] p-4 shadow-[var(--shadow-popover)] sm:p-6'>
        {completion ? <>
          <div className='text-center'><span className='mx-auto flex h-12 w-12 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-soft)] text-xl text-[var(--brand)]'><FiShield /></span><h1 className='mt-3 text-xl font-bold text-[var(--text-primary)] sm:text-2xl'>{t('Save your recovery codes')}</h1><p className='mt-2 text-sm text-[var(--text-secondary)]'>{t('These recovery codes are shown only once. Store them in a secure place.')}</p></div>
          <div className='mt-5 grid grid-cols-2 gap-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-3'>{completion.recoveryCodes.map((recoveryCode) => <code key={recoveryCode} className='rounded bg-[var(--surface-panel)] px-2 py-2 text-center text-xs text-[var(--text-primary)]'>{recoveryCode}</code>)}</div>
          <Button className='mt-4 w-full' variant='secondary' onClick={() => copy(completion.recoveryCodes.join('\n'))}><FiCopy />{t('Copy')}</Button>
          <Button className='mt-2 w-full' variant='primary' onClick={() => completeLogin(completion)}>{t('I saved my recovery codes')}</Button>
        </> : <>
          <div className='text-center'><span className='mx-auto flex h-12 w-12 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-soft)] text-xl text-[var(--brand)]'>{action === 'setup' ? <FiSmartphone /> : <FiShield />}</span><p className='mt-3 text-sm font-semibold text-[var(--brand)]'>{t('Billing Management')}</p><h1 className='mt-1 text-xl font-bold text-[var(--text-primary)] sm:text-2xl'>{title}</h1><p className='mx-auto mt-2 max-w-[360px] text-sm leading-5 text-[var(--text-secondary)]'>{description}</p></div>
          {error && <p className='mt-5 rounded-[var(--radius-md)] border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-[var(--danger)]' role='alert'>{error}</p>}
          {action === 'setup' && !setup && !error && <div className='mt-6 animate-pulse rounded-[var(--radius-md)] bg-[var(--surface-panel-muted)] p-10 text-center text-sm text-[var(--text-muted)]'>{t('Loading')}</div>}
          {action !== 'setup' || setup ? <form className='mt-4 space-y-3' onSubmit={submit} noValidate>
            {action === 'setup' && setup && (
              <>
                <img src={setup.qrCode} alt={t('QR code for two-factor authentication')} className='mx-auto aspect-square w-[min(100%,8.5rem)] rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-white p-2 sm:w-40' />
                <div className='rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-3'>
                  <p className='text-xs text-[var(--text-muted)]'>{t('Manual setup key')}</p>
                  <div className='mt-1 flex items-center gap-2'>
                    <code className='min-w-0 flex-1 break-all text-xs text-[var(--text-primary)]'>{setup.secret}</code>
                    <button type='button' onClick={() => copy(setup.secret)} className='text-[var(--brand)]' aria-label={t('Copy')}><FiCopy /></button>
                  </div>
                </div>
              </>
            )}
            <FormField label={useRecoveryCode ? t('Recovery code') : t('Authentication code')}>
              {useRecoveryCode ? <div className='relative'><FiKey className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' /><input autoFocus inputMode='text' autoComplete='one-time-code' value={code} onChange={(event) => setCode(event.target.value)} className='login-input h-12 w-full rounded-[var(--radius-md)] border border-[var(--border-strong)] bg-[var(--surface-panel-muted)] px-4 pl-12 text-center text-base text-[var(--text-primary)] sm:h-[52px] sm:text-lg' placeholder='ABCD-EFGH-IJKL-MNOP' /></div> : (
                <div className='mx-auto grid w-fit grid-cols-6 gap-2' role='group' aria-label={t('Authentication code')}>
                  {otpDigits.map((digit, index) => <input
                    key={index}
                    ref={(node) => { otpInputRefs.current[index] = node; }}
                    autoFocus={index === 0}
                    inputMode='numeric'
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    aria-label={`${t('Authentication code')} ${index + 1}`}
                    value={digit}
                    maxLength={6}
                    onChange={(event) => updateOtpDigits(index, event.target.value)}
                    onKeyDown={(event) => handleOtpKeyDown(index, event)}
                    onPaste={(event) => { event.preventDefault(); updateOtpDigits(index, event.clipboardData.getData('text')); }}
                    className='login-input h-12 w-10 rounded-[var(--radius-md)] border border-[var(--border-strong)] bg-[var(--surface-panel-muted)] text-center text-lg font-semibold text-[var(--text-primary)] sm:h-[52px] sm:w-11 sm:text-xl'
                  />)}
                </div>
              )}
            </FormField>
            <Button type='submit' variant='primary' className='h-[52px] w-full text-base' loading={submitting}>{t(action === 'setup' ? 'Verify and finish setup' : 'Verify and sign in')}</Button>
            {action !== 'setup' && <button type='button' className='mx-auto block text-sm font-semibold text-[var(--brand-strong)] hover:underline' onClick={() => { setUseRecoveryCode((value) => !value); setCode(''); setOtpDigits(emptyOtpDigits()); }}>{useRecoveryCode ? t('Use authenticator code') : t('Use a recovery code')}</button>}
          </form> : null}
          <button type='button' className='mx-auto mt-5 flex items-center gap-2 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]' onClick={cancel}><FiArrowLeft />{t('Back to sign in')}</button>
        </>}
      </section>
      </div>
    </main>
  );
};
